import { Plus, Minus, UserPlus, Check, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { PersonRow } from './PersonRow';
import { cn } from '@/lib/utils';
import type { Bemanning, TalentBehovItem } from '../../services/api';

interface TaskCardProps {
  task: TalentBehovItem;
  people: Bemanning[];
  draftPeople?: any[];
  removedPeopleIds?: Set<number>;
  onUpdateCount: (delta: number) => void;
  onAssignPerson: () => void;
  onRemovePerson?: (id: number) => void;
  className?: string;
  readOnly?: boolean;
}

export function TaskCard({ 
  task, 
  people,
  draftPeople = [],
  removedPeopleIds = new Set(),
  onUpdateCount,
  onAssignPerson,
  onRemovePerson,
  className,
  readOnly = false,
}: TaskCardProps) {
  const confirmed = people.filter(p => p.status === 'bekreftet').length;
  const waiting = people.filter(p => p.status === 'planlagt' || p.status === 'ikke_svart' || !p.status).length;
  const declined = people.filter(p => p.status === 'avvist').length;
  const missingCount = Math.max(0, task.antall - confirmed);

  return (
    <Card className={cn('hover:shadow-md transition-all border-2', className)}>
      <CardHeader className="pb-3">
        {/* Mobile: Stack vertikalt, Desktop: Horisontal */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h4 className="font-bold text-sm sm:text-base text-foreground">{task.talent_navn}</h4>
          
          {/* Statustekst (kun informasjon, ingen knapper) */}
          <div className="flex items-center gap-3 flex-wrap text-xs sm:text-sm text-muted-foreground">
            {confirmed > 0 && (
              <span className="inline-flex items-center gap-1 text-status-confirmed font-medium" title={`${confirmed} bekreftet`}>
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                {confirmed}
                <span className="sr-only sm:not-sr-only">bekreftet</span>
              </span>
            )}
            {waiting > 0 && (
              <span className="inline-flex items-center gap-1 text-status-waiting font-medium" title={`${waiting} venter`}>
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {waiting}
                <span className="sr-only sm:not-sr-only">venter</span>
              </span>
            )}
            {declined > 0 && (
              <span className="inline-flex items-center gap-1 text-status-declined font-medium" title={`${declined} avslått`}>
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                {declined}
                <span className="sr-only sm:not-sr-only">avslått</span>
              </span>
            )}
            {missingCount > 0 && (
              <span className="text-status-waiting font-medium">{missingCount} mangler</span>
            )}
          </div>
        </div>
      </CardHeader>

      {/* People list */}
      {(people.length > 0 || draftPeople.length > 0) ? (
        <CardContent className="pt-0 space-y-2">
          {/* Persisterte personer */}
          {people.map((person) => {
            const isMarkedForRemoval = removedPeopleIds.has(person.id);
            const handleRemove = !readOnly && onRemovePerson
              ? () => onRemovePerson(person.id)
              : undefined;
            return (
              <PersonRow
                key={person.id}
                person={person}
                isMarkedForRemoval={isMarkedForRemoval}
                onRemove={handleRemove}
              />
            );
          })}
          
          {/* Draft-personer (nye) */}
          {draftPeople.map((person: any) => {
            const handleRemove = !readOnly && onRemovePerson
              ? () => onRemovePerson(person.id)
              : undefined;
            return (
              <PersonRow
                key={`draft-${person.id}`}
                person={person}
                isDraft
                onRemove={handleRemove}
              />
            );
          })}
        </CardContent>
      ) : (
        <CardContent className="pt-0">
          <div className="p-3 border-2 border-dashed border-muted rounded-lg text-center text-muted-foreground text-sm">
            Ingen personer tildelt ennå
          </div>
        </CardContent>
      )}

      {/* Actions - Mobile: Stack vertikalt, Desktop: Horisontal */}
      {!readOnly && (
        <CardContent className="pt-3 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button 
              onClick={onAssignPerson}
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <UserPlus className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Tildel person</span>
            </Button>
            
            <div className="flex items-center gap-2 sm:ml-auto">
              <span className="text-xs sm:text-sm text-muted-foreground font-medium whitespace-nowrap">Antall:</span>
              <div className="flex items-center gap-0.5 bg-muted rounded-md px-1.5 py-1">
                <Button 
                  variant="ghost"
                  size="icon"
                  onClick={() => onUpdateCount(-1)}
                  className="h-6 w-6 sm:h-7 sm:w-7"
                >
                  <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
                <span className="px-2 sm:px-3 text-sm sm:text-base font-bold text-foreground min-w-[28px] sm:min-w-[32px] text-center">
                  {task.antall}
                </span>
                <Button 
                  variant="ghost"
                  size="icon"
                  onClick={() => onUpdateCount(1)}
                  className="h-6 w-6 sm:h-7 sm:w-7"
                >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

